import { useState, type ReactNode } from 'react';
import { createAlertRule, fetchAlertRules, fetchTaskTypes, updateAlertRule } from '../api/client';
import { ApiError } from '../api/request';
import type { AlertRuleResponse, TaskTypeResponse } from '../api/types';
import { useSession } from '../app/WorkforceContext';
import { useAsync } from '../app/useAsync';
import { ErrorCard, LoadingState } from '../states';
import {
  FORM_ATURAN_AWAL,
  LABEL_METRIK,
  LABEL_OPERATOR,
  LABEL_SEVERITY,
  METRIK_ALERT,
  OPERATOR_ALERT,
  SEVERITY_ALERT,
  type FormAturan,
  bodyDariForm,
  masalahFormAturan,
} from '../lib/alert-rule-form';
import type { AuthState } from '../api/request';

const SEVERITY_TONE: Record<string, { bg: string; fg: string }> = {
  critical: { bg: 'var(--danger-bg)', fg: 'var(--danger)' },
  warning: { bg: 'var(--warn-bg)', fg: 'var(--warn)' },
  info: { bg: 'var(--neutral-bg)', fg: 'var(--muted)' },
};

function SeverityBadge({
  severity,
}: {
  readonly severity: AlertRuleResponse['severity'];
}): ReactNode {
  const t = SEVERITY_TONE[severity] ?? SEVERITY_TONE.info!;
  return (
    <span
      style={{
        display: 'inline-flex',
        padding: '0.15rem 0.55rem',
        borderRadius: 'var(--r-pill)',
        fontSize: '0.75rem',
        fontWeight: 700,
        background: t.bg,
        color: t.fg,
      }}
    >
      {LABEL_SEVERITY[severity]}
    </span>
  );
}

/** Form tambah/ubah aturan — dipakai sekali untuk tambah, dan per baris untuk ubah. */
function RuleForm({
  awal,
  taskTypes,
  busy,
  onBatal,
  onSimpan,
}: {
  readonly awal: FormAturan;
  readonly taskTypes: readonly TaskTypeResponse[];
  readonly busy: boolean;
  readonly onBatal: () => void;
  readonly onSimpan: (form: FormAturan) => void;
}): ReactNode {
  const [form, setForm] = useState<FormAturan>(awal);
  const masalah = masalahFormAturan(form);
  const set = <K extends keyof FormAturan>(k: K, v: FormAturan[K]): void =>
    setForm((f) => ({ ...f, [k]: v }));

  return (
    <div style={{ display: 'grid', gap: '0.6rem', marginTop: '0.75rem' }}>
      <div className="row">
        <label style={{ flex: 1 }}>
          <span className="muted small">Metrik</span>
          <select
            value={form.metricKey}
            onChange={(e) => set('metricKey', e.target.value)}
            style={{ width: '100%' }}
          >
            {METRIK_ALERT.map((m) => (
              <option key={m} value={m}>
                {LABEL_METRIK[m] ?? m}
              </option>
            ))}
          </select>
        </label>
        <label style={{ width: '5.5rem' }}>
          <span className="muted small">Operator</span>
          <select
            value={form.operator}
            onChange={(e) => set('operator', e.target.value as FormAturan['operator'])}
            style={{ width: '100%' }}
          >
            {OPERATOR_ALERT.map((o) => (
              <option key={o} value={o}>
                {LABEL_OPERATOR[o]}
              </option>
            ))}
          </select>
        </label>
        <label style={{ width: '6rem' }}>
          <span className="muted small">Ambang</span>
          <input
            type="number"
            inputMode="decimal"
            value={form.threshold}
            onChange={(e) => set('threshold', e.target.value)}
            style={{ width: '100%' }}
          />
        </label>
      </div>
      <label>
        <span className="muted small">Keparahan</span>
        <select
          value={form.severity}
          onChange={(e) => set('severity', e.target.value as FormAturan['severity'])}
          style={{ width: '100%' }}
        >
          {SEVERITY_ALERT.map((s) => (
            <option key={s} value={s}>
              {LABEL_SEVERITY[s]}
            </option>
          ))}
        </select>
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <input
          type="checkbox"
          checked={form.autoCreateTask}
          disabled={taskTypes.length === 0}
          onChange={(e) => set('autoCreateTask', e.target.checked)}
        />
        <span className="small">
          Buat tiket servis otomatis saat terpicu
          {taskTypes.length === 0 ? ' (perlu jenis tugas terkonfigurasi)' : ''}
        </span>
      </label>
      {form.autoCreateTask && taskTypes.length > 0 ? (
        <label>
          <span className="muted small">Jenis tugas</span>
          <select
            value={form.taskTypeCode}
            onChange={(e) => set('taskTypeCode', e.target.value)}
            style={{ width: '100%' }}
          >
            <option value="">— pilih —</option>
            {taskTypes.map((t) => (
              <option key={t.id} value={t.code}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {masalah ? <p className="warn-text small">{masalah}</p> : null}
      <div className="row">
        <button type="button" disabled={busy || masalah !== null} onClick={() => onSimpan(form)}>
          {busy ? 'Menyimpan…' : 'Simpan'}
        </button>
        <button type="button" className="chip" onClick={onBatal}>
          Batal
        </button>
      </div>
    </div>
  );
}

/** Baris satu aturan + aksi ubah/nonaktifkan. */
function RuleRow({
  rule,
  taskTypes,
  auth,
  onChanged,
}: {
  readonly rule: AlertRuleResponse;
  readonly taskTypes: readonly TaskTypeResponse[];
  readonly auth: AuthState;
  readonly onChanged: () => void;
}): ReactNode {
  const [edit, setEdit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [galat, setGalat] = useState<ApiError | null>(null);

  async function kirim(fn: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    setGalat(null);
    try {
      await fn();
      setEdit(false);
      onChanged();
    } catch (e) {
      setGalat(e instanceof ApiError ? e : new ApiError(0, 'ERROR', 'Gagal menyimpan.', null));
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="list-item" style={{ opacity: rule.enabled ? 1 : 0.55 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '0.5rem',
          alignItems: 'center',
        }}
      >
        <strong>
          {LABEL_METRIK[rule.metricKey] ?? rule.metricKey} {LABEL_OPERATOR[rule.operator]}{' '}
          {rule.threshold}
        </strong>
        <SeverityBadge severity={rule.severity} />
      </div>
      <div className="row" style={{ alignItems: 'center' }}>
        {rule.autoCreateTask ? <span className="muted small">→ tiket otomatis</span> : null}
        {!rule.enabled ? <span className="muted small">Nonaktif</span> : null}
        <span style={{ flex: 1 }} />
        <button
          type="button"
          className="chip"
          disabled={busy}
          onClick={() =>
            void kirim(() => updateAlertRule(auth, rule.id, { enabled: !rule.enabled }))
          }
        >
          {rule.enabled ? 'Nonaktifkan' : 'Aktifkan'}
        </button>
        <button type="button" className="chip" onClick={() => setEdit((v) => !v)}>
          {edit ? 'Tutup' : 'Ubah'}
        </button>
      </div>
      {galat ? <ErrorCard error={galat} onRetry={() => setGalat(null)} /> : null}
      {edit ? (
        <RuleForm
          awal={{
            metricKey: rule.metricKey,
            operator: rule.operator,
            threshold: String(rule.threshold),
            severity: rule.severity,
            autoCreateTask: rule.autoCreateTask,
            taskTypeCode: rule.taskTypeCode ?? '',
          }}
          taskTypes={taskTypes}
          busy={busy}
          onBatal={() => setEdit(false)}
          onSimpan={(form) => {
            const body = bodyDariForm(form);
            // PATCH tak menerima metricKey/assetId (hanya operator/threshold/severity/autoCreateTask/
            // taskTypeCode/enabled); kirim subset yang boleh diubah.
            void kirim(() =>
              updateAlertRule(auth, rule.id, {
                operator: body.operator,
                threshold: body.threshold,
                severity: body.severity,
                autoCreateTask: body.autoCreateTask,
                ...(body.taskTypeCode ? { taskTypeCode: body.taskTypeCode } : {}),
              }),
            );
          }}
        />
      ) : null}
    </li>
  );
}

/**
 * Pengaturan aturan alert (ADR-0055 Fase 3 slice 4) — kelola ambang telemetri.
 *
 * Seluruh endpoint aturan dijaga `workforce.asset.update`, jadi halaman & menunya hanya untuk
 * peran itu (MANAGER/WORKFORCE_ADMIN). TIDAK ada hapus di server — aturan dinonaktifkan via PATCH
 * `enabled:false` (kejadian ber-FK restrict ke aturan). Jenis tugas (untuk tiket otomatis) dimuat
 * best-effort; bila gagal/kurang izin, opsi tiket otomatis dinonaktifkan dengan keterangan.
 */
export function AlertRulesPage(): ReactNode {
  const { auth } = useSession();
  const [tambah, setTambah] = useState(false);
  const [busyTambah, setBusyTambah] = useState(false);
  const [galatTambah, setGalatTambah] = useState<ApiError | null>(null);

  const { state, reload } = useAsync<readonly AlertRuleResponse[]>(
    () => fetchAlertRules(auth),
    [auth],
  );
  // Jenis tugas best-effort: kegagalan (mis. kurang izin) → daftar kosong, bukan menjatuhkan layar.
  const { state: ttState } = useAsync<readonly TaskTypeResponse[]>(
    () => fetchTaskTypes(auth).catch(() => []),
    [auth],
  );
  const taskTypes = ttState.status === 'ready' ? ttState.data : [];

  if (state.status === 'loading') return <LoadingState label="Memuat aturan alert…" />;
  if (state.status === 'error') return <ErrorCard error={state.error} onRetry={reload} />;

  const rules = state.data;

  async function simpanBaru(form: FormAturan): Promise<void> {
    setBusyTambah(true);
    setGalatTambah(null);
    try {
      await createAlertRule(auth, bodyDariForm(form));
      setTambah(false);
      reload();
    } catch (e) {
      setGalatTambah(
        e instanceof ApiError ? e : new ApiError(0, 'ERROR', 'Gagal membuat aturan.', null),
      );
    } finally {
      setBusyTambah(false);
    }
  }

  return (
    <>
      <section className="card">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <h2 style={{ margin: 0 }}>Aturan alert</h2>
          {!tambah ? (
            <button type="button" onClick={() => setTambah(true)}>
              + Tambah
            </button>
          ) : null}
        </div>
        <p className="muted small">
          Ambang memicu alert otomatis saat telemetri alat melewati batas. Aturan tanpa alat berlaku
          untuk seluruh armada organisasi.
        </p>
        {tambah ? (
          <>
            {galatTambah ? (
              <ErrorCard error={galatTambah} onRetry={() => setGalatTambah(null)} />
            ) : null}
            <RuleForm
              awal={FORM_ATURAN_AWAL}
              taskTypes={taskTypes}
              busy={busyTambah}
              onBatal={() => setTambah(false)}
              onSimpan={(form) => void simpanBaru(form)}
            />
          </>
        ) : null}
      </section>

      {rules.length === 0 ? (
        <section className="card">
          <p className="muted">
            Belum ada aturan. Tambahkan ambang (mis. Disk &gt; 90%) agar alat yang melewatinya
            memicu alert.
          </p>
        </section>
      ) : (
        <section className="card">
          <ul className="list">
            {rules.map((r) => (
              <RuleRow key={r.id} rule={r} taskTypes={taskTypes} auth={auth} onChanged={reload} />
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
